import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { adminOrdersAPI } from "../services/api";
import { Link } from "react-router-dom";
import { Filter, BarChart3, Printer, Eye } from 'lucide-react';
import CustomSelect from './CustomSelect';
import OrderItemsModal from './OrderItemsModal';
// Uses global header via Layout

function debounce(fn, ms) {
  let timeout;
  return function (...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(this, args), ms);
  };
}

const AdminOrders = () => {
  const { isAdmin, user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [stats, setStats] = useState({});
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [filterOrderId, setFilterOrderId] = useState("");
  const [filterPaymentStatus, setFilterPaymentStatus] = useState("");
  const [filterPaymentRef, setFilterPaymentRef] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [showFiltersMobile, setShowFiltersMobile] = useState(false);
  const [showStatsMobile, setShowStatsMobile] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const innerFetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      const currentFilters = {
        order_id: filterOrderId,
        payment_status: filterPaymentStatus,
        payment_reference: filterPaymentRef,
        date_from: filterDateFrom,
        date_to: filterDateTo,
        status: selectedStatus === "all" ? "" : selectedStatus,
      };
      Object.keys(currentFilters).forEach((k) => {
        if (currentFilters[k]) params[k] = currentFilters[k];
      });
      const response = await adminOrdersAPI.getAll(params);
      setOrders(response.data.orders);
      setStats(response.data);
    } catch (err) {
      setError("Failed to fetch orders");
      console.error("Error fetching orders:", err);
    } finally {
      setLoading(false);
    }
  }, [
    filterOrderId,
    filterPaymentStatus,
    filterPaymentRef,
    filterDateFrom,
    filterDateTo,
    selectedStatus,
  ]);

  const debouncedFetchOrdersRef = useRef();
  
  // Update the debounced function whenever innerFetchOrders changes
  useEffect(() => {
    debouncedFetchOrdersRef.current = debounce(innerFetchOrders, 350);
  }, [innerFetchOrders]);

  const debouncedFetchOrders = useCallback(() => {
    if (debouncedFetchOrdersRef.current) {
      debouncedFetchOrdersRef.current();
    }
  }, []);

  useEffect(() => {
    if (!isAdmin()) {
      setError("Access denied. Admin privileges required.");
      setLoading(false);
      return;
    }
    debouncedFetchOrders();
  }, [
    filterOrderId,
    filterPaymentStatus,
    filterPaymentRef,
    filterDateFrom,
    filterDateTo,
    selectedStatus,
    isAdmin,
    debouncedFetchOrders,
  ]);

  const handleStatusUpdate = async (orderId, newStatus) => {
    try {
      await adminOrdersAPI.update(orderId, { status: newStatus });
      innerFetchOrders();
    } catch (err) {
      console.error("Error updating order status:", err);
      alert("Failed to update order status");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "confirmed":
        return "bg-blue-100 text-blue-800";
      case "shipped":
        return "bg-purple-100 text-purple-800";
      case "delivered":
        return "bg-green-100 text-green-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const filteredOrders =
    selectedStatus === "all"
      ? orders
      : orders.filter((order) => order.status === selectedStatus);

  const handlePaymentStatusUpdate = async (orderId, newPaymentStatus) => {
    try {
      await adminOrdersAPI.update(orderId, { payment_status: newPaymentStatus });
      innerFetchOrders();
    } catch (err) {
      console.error("Error updating payment status:", err);
      alert("Failed to update payment status");
    }
  };

  const handleViewItems = async (orderId) => {
    try {
      const response = await adminOrdersAPI.getById(orderId);
      setSelectedOrder(response.data);
      setIsModalOpen(true);
    } catch (err) {
      console.error("Error fetching order details:", err);
      alert("Failed to load order details");
    }
  };

  if (!isAdmin()) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h1>
          <p className="text-gray-600">
            You need admin privileges to access this page.
          </p>
          <Link
            to="/dashboard"
            className="text-blue-600 hover:text-blue-800 mt-4 inline-block"
          >
            Go to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden">
      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Stats/Counts and Filter Toggle Buttons (Mobile only, Admin only) */}
        {user?.role === 'admin' && (
          <div className="md:hidden mb-4 flex justify-end gap-2">
            <button
              onClick={() => setShowStatsMobile(!showStatsMobile)}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 text-gray-700"
              aria-label="Toggle Stats"
            >
              <BarChart3 className="h-5 w-5" />
              <span className="text-sm font-medium">Stats</span>
            </button>
            <button
              onClick={() => setShowFiltersMobile(!showFiltersMobile)}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 text-gray-700"
              aria-label="Toggle Filters"
            >
              <Filter className="h-5 w-5" />
              <span className="text-sm font-medium">Filters</span>
            </button>
          </div>
        )}

        {/* Stats */}
        <div className={`grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4 mb-6 ${
          user?.role === 'admin' 
            ? (showStatsMobile ? '' : 'hidden md:grid')
            : ''
        }`}>
          {[
            { label: "Total", value: stats.total_count, color: "text-gray-900" },
            { label: "Pending", value: stats.pending_count, color: "text-yellow-600" },
            { label: "Confirmed", value: stats.confirmed_count, color: "text-blue-600" },
            { label: "Shipped", value: stats.shipped_count, color: "text-purple-600" },
            { label: "Delivered", value: stats.delivered_count, color: "text-green-600" },
            { label: "Cancelled", value: stats.cancelled_count, color: "text-red-600" },
            { label: "Today Revenue", value: `₹${parseFloat(stats.daily_revenue || 0).toLocaleString('en-IN', {minimumFractionDigits: 2})}`, color: "text-green-800 font-semibold" },
            { label: "Month Revenue", value: `₹${parseFloat(stats.monthly_revenue || 0).toLocaleString('en-IN', {minimumFractionDigits: 2})}`, color: "text-blue-800 font-semibold" },
          ].map((item, i) => (
            <div key={i} className="bg-white rounded-lg shadow p-3 sm:p-4 text-center">
              <div className={`text-2xl font-bold ${item.color}`}>
                {item.value || 0}
              </div>
              <div className="text-xs sm:text-sm text-gray-600">{item.label}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className={`bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-6 sm:mb-8 overflow-visible ${
          user?.role === 'admin' 
            ? (showFiltersMobile ? 'block' : 'hidden md:block')
            : ''
        }`}>
          <form className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 sm:gap-3 items-end">
            <div className="w-full">
              <label className="text-xs font-medium text-gray-700 mb-1 block">
                Order ID
              </label>
              <input
                type="text"
                value={filterOrderId}
                onChange={(e) => setFilterOrderId(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="#ID"
              />
            </div>
            <div className="w-full">
              <CustomSelect
                id="payment-filter"
                value={filterPaymentStatus}
                onChange={(e) => setFilterPaymentStatus(e.target.value)}
                label="Payment"
                size="sm"
                options={[
                  { value: '', label: 'Any' },
                  { value: 'pending', label: 'Pending' },
                  { value: 'success', label: 'Success' },
                  { value: 'failed', label: 'Failed' },
                  { value: 'cancelled', label: 'Cancelled' }
                ]}
              />
            </div>
            <div className="w-full">
              <label className="text-xs font-medium text-gray-700 mb-1 block">
                Ref #
              </label>
              <input
                type="text"
                value={filterPaymentRef}
                onChange={(e) => setFilterPaymentRef(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Reference"
              />
            </div>
            <div className="w-full">
              <label className="text-xs font-medium text-gray-700 mb-1 block">
                From Date
              </label>
              <input
                type="date"
                value={filterDateFrom}
                onChange={(e) => setFilterDateFrom(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
            <div className="w-full">
              <label className="text-xs font-medium text-gray-700 mb-1 block">
                To Date
              </label>
              <input
                type="date"
                value={filterDateTo}
                onChange={(e) => setFilterDateTo(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
            <div className="w-full">
              <CustomSelect
                id="status-filter"
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                label="Status"
                size="sm"
                options={["all", "pending", "confirmed", "shipped", "delivered", "cancelled"].map(
                  (s) => ({
                    value: s,
                    label: s.charAt(0).toUpperCase() + s.slice(1)
                  })
                )}
              />
            </div>
          </form>
        </div>

        {/* Orders */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin h-12 w-12 border-b-2 border-green-600 rounded-full" />
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <p className="text-red-700">{error}</p>
            <button
              onClick={debouncedFetchOrders}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-full hover:bg-red-700"
            >
              Try Again
            </button>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
            <p className="text-gray-600 text-lg">No orders found</p>
            <p className="text-gray-500">Try adjusting your filters</p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block bg-white shadow rounded-lg overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    {[
                      "Order ID",
                      "Customer",
                      "Amount",
                      "Payment",
                      "Status",
                      "Date",
                      "Actions",
                    ].map((header) => (
                      <th
                        key={header}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase"
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredOrders.map((order) => (
                    <tr key={order.id}>
                      <td className="px-6 py-3 font-medium text-gray-900">
                        #{order.id}
                      </td>
                      <td className="px-6 py-3">
                        <div>
                          <div className="font-medium">{order.user.name}</div>
                          <div className="text-gray-500 text-xs">{order.user.email}</div>
                        </div>
                      </td>
                      <td className="px-6 py-3">{order.formatted_total_amount || `₹${parseFloat(order.total_amount || 0).toFixed(2)}`}</td>
                      <td className="px-6 py-3 text-xs">
                        <div className="mb-1">Status: {order.payment_status}</div>
                        <div className="mb-2">Ref: {order.payment_reference || "-"}</div>
                        <div className="space-x-2">
                          <button
                            onClick={() => handlePaymentStatusUpdate(order.id, "success")}
                            className="px-2 py-1 rounded-full bg-green-100 text-green-700 hover:bg-green-200"
                          >
                            Mark Paid
                          </button>
                          <button
                            onClick={() => handlePaymentStatusUpdate(order.id, "failed")}
                            className="px-2 py-1 rounded-full bg-red-100 text-red-700 hover:bg-red-200"
                          >
                            Mark Failed
                          </button>
                          <button
                            onClick={() => handlePaymentStatusUpdate(order.id, "cancelled")}
                            className="px-2 py-1 rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200"
                          >
                            Cancelled
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-3">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                            order.status
                          )}`}
                        >
                          {order.status}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-sm">
                        {new Date(order.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-3 space-x-4">
                        <button
                          onClick={() => handleViewItems(order.id)}
                          className="inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors"
                          title="View Items"
                        >
                          <Eye className="w-5 h-5" strokeWidth={1.5} />
                        </button>
                        {(order.payment_status === 'success' || order.payment_status === 'paid' || ['confirmed', 'shipped', 'delivered'].includes(order.status)) && (
                          <Link
                            to={`/orders/${order.id}/invoice`}
                            target="_blank"
                            className="inline-flex items-center text-emerald-600 hover:text-emerald-800 transition-colors"
                            title="View Invoice"
                          >
                            <Printer className="w-5 h-5" strokeWidth={1.5} />
                          </Link>
                        )}
                        {order.status === "pending" && (
                          <>
                            <button
                              onClick={() => handleStatusUpdate(order.id, "confirmed")}
                              className="text-blue-600 hover:underline"
                            >
                              Confirm
                            </button>
                            <button
                              onClick={() => handleStatusUpdate(order.id, "cancelled")}
                              className="text-red-600 hover:underline"
                            >
                              Cancel
                            </button>
                          </>
                        )}
                        {order.status === "confirmed" && (
                          <button
                            onClick={() => handleStatusUpdate(order.id, "shipped")}
                            className="text-purple-600 hover:underline"
                          >
                            Ship
                          </button>
                        )}
                        {order.status === "shipped" && (
                          <button
                            onClick={() => handleStatusUpdate(order.id, "delivered")}
                            className="text-green-600 hover:underline"
                          >
                            Deliver
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden space-y-4">
              {filteredOrders.map((order) => (
                <div
                  key={order.id}
                  className="bg-white rounded-lg shadow p-4 text-sm space-y-2"
                >
                  <div className="flex justify-between">
                    <span className="font-semibold text-gray-800">
                      Order #{order.id}
                    </span>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(
                        order.status
                      )}`}
                    >
                      {order.status}
                    </span>
                  </div>
                  <div>
                    <div className="font-medium">{order.user.name}</div>
                    <div className="text-gray-500 text-xs">{order.user.email}</div>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>{order.formatted_total_amount || `₹${parseFloat(order.total_amount || 0).toFixed(2)}`}</span>
                    <span>{new Date(order.created_at).toLocaleDateString()}</span>
                  </div>
                  <div className="text-xs text-gray-500">
                    Payment: {order.payment_status || "pending"} | Ref: {order.payment_reference || "-"}
                  </div>
                  <div className="flex flex-wrap gap-2 pt-2 text-xs">
                    <button
                      onClick={() => handlePaymentStatusUpdate(order.id, "success")}
                      className="px-2 py-1 rounded-full bg-green-100 text-green-700"
                    >
                      Mark Paid
                    </button>
                    <button
                      onClick={() => handlePaymentStatusUpdate(order.id, "failed")}
                      className="px-2 py-1 rounded-full bg-red-100 text-red-700"
                    >
                      Mark Failed
                    </button>
                    <button
                      onClick={() => handlePaymentStatusUpdate(order.id, "cancelled")}
                      className="px-2 py-1 rounded-full bg-gray-100 text-gray-700"
                    >
                      Cancelled
                    </button>
                  </div>
                  <div className="flex items-center space-x-4 pt-4 border-t border-gray-100 mt-2">
                      <button
                        onClick={() => handleViewItems(order.id)}
                        className="inline-flex items-center gap-2 text-blue-600 font-medium"
                      >
                        <Eye className="w-4 h-4" strokeWidth={1.5} />
                        Items
                      </button>
                    {(order.payment_status === 'success' || order.payment_status === 'paid' || ['confirmed', 'shipped', 'delivered'].includes(order.status)) && (
                      <Link
                        to={`/orders/${order.id}/invoice`}
                        target="_blank"
                        className="inline-flex items-center gap-2 text-emerald-600 font-medium"
                      >
                        <Printer className="w-4 h-4" strokeWidth={1.5} />
                        Invoice
                      </Link>
                    )}
                  </div>
                  <div className="flex space-x-4 pt-2">
                    {order.status === "pending" && (
                      <>
                        <button
                          onClick={() => handleStatusUpdate(order.id, "confirmed")}
                          className="text-blue-600"
                        >
                          Confirm
                        </button>
                        <button
                          onClick={() => handleStatusUpdate(order.id, "cancelled")}
                          className="text-red-600"
                        >
                          Cancel
                        </button>
                      </>
                    )}
                    {order.status === "confirmed" && (
                      <button
                        onClick={() => handleStatusUpdate(order.id, "shipped")}
                        className="text-purple-600"
                      >
                        Ship
                      </button>
                    )}
                    {order.status === "shipped" && (
                      <button
                        onClick={() => handleStatusUpdate(order.id, "delivered")}
                        className="text-green-600"
                      >
                        Deliver
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
      {isModalOpen && (
        <OrderItemsModal
          order={selectedOrder}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminOrders;
